/**
 * Export Button Component
 *
 * Reusable export button with multiple format support and column selection.
 * Supports CSV, Excel (CSV format), and JSON exports.
 */

import React, { useState, useRef, useEffect, useMemo } from "react";
import * as XLSX from "xlsx";
import { motion, AnimatePresence } from "framer-motion";
import {
  Download,
  FileSpreadsheet,
  FileJson,
  FileText,
  ChevronDown,
  Check,
  Settings2,
  ChevronLeft,
  Columns3,
  CheckSquare,
  Square,
  MinusSquare,
} from "lucide-react";

// =============================================================================
// TYPES
// =============================================================================

export type ExportFormat = "csv" | "excel" | "json";

export interface ExportColumn<T> {
  /** The key in the data object */
  key: keyof T | string;
  /** The header label for the export */
  header: string;
  /** Optional transform function for the value */
  transform?: (value: unknown, row: T) => string | number | boolean;
  /** Default selected state (default: true) */
  defaultSelected?: boolean;
}

export interface ExportButtonProps<T> {
  /** Data to export */
  data: T[];
  /** Optional async resolver to fetch export data on demand */
  resolveData?: () => Promise<T[]>;
  /** Column definitions for export */
  columns: ExportColumn<T>[];
  /** Base filename (without extension) */
  filename?: string;
  /** Available export formats */
  formats?: ExportFormat[];
  /** Allow users to select which columns to export */
  allowColumnSelection?: boolean;
  /** Persist column selection to localStorage */
  persistSelection?: boolean;
  /** Unique key for persisting selection */
  storageKey?: string;
  /** Callback before export - return false to cancel */
  onBeforeExport?: (
    format: ExportFormat,
    selectedColumns: ExportColumn<T>[],
  ) => boolean | Promise<boolean>;
  /** Callback after successful export */
  onExportComplete?: (
    format: ExportFormat,
    selectedColumns: ExportColumn<T>[],
  ) => void;
  /** Custom button text */
  buttonText?: string;
  /** Show button text on mobile */
  showTextOnMobile?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Additional class names */
  className?: string;
  /** Button variant */
  variant?: "default" | "primary" | "ghost";
  /** Button size */
  size?: "sm" | "md" | "lg";
  /** Disable hover/tap scale animation */
  disableScaleAnimation?: boolean;
  /** Dropdown menu alignment relative to button */
  dropdownAlign?: "left" | "right";
  /** Extra content rendered in the formats dropdown (e.g. status picker) */
  dropdownExtra?: React.ReactNode;
}

type ViewState = "formats" | "columns";

// =============================================================================
// FORMAT CONFIGS
// =============================================================================

const formatConfig: Record<
  ExportFormat,
  { label: string; icon: React.ReactNode; extension: string }
> = {
  csv: {
    label: "CSV",
    icon: <FileText size={16} />,
    extension: "csv",
  },
  excel: {
    label: "Excel",
    icon: <FileSpreadsheet size={16} />,
    extension: "xlsx",
  },
  json: {
    label: "JSON",
    icon: <FileJson size={16} />,
    extension: "json",
  },
};

const sizeClasses = {
  sm: "px-3 py-1.5 text-xs gap-1.5",
  md: "px-4 py-2.5 text-sm gap-2",
  lg: "px-5 py-3 text-base gap-2.5",
};

const variantClasses = {
  default:
    "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300",
  primary:
    "bg-gradient-to-r from-primary-500 to-primary-700 text-white shadow-lg shadow-primary-500/25 hover:shadow-xl",
  ghost: "bg-transparent text-gray-700 hover:bg-gray-100",
};

// =============================================================================
// EXPORT UTILITIES
// =============================================================================

function getNestedValue<T>(obj: T, path: string): unknown {
  return path.split(".").reduce((acc: unknown, part: string) => {
    if (acc && typeof acc === "object" && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }
    return undefined;
  }, obj);
}

/**
 * Property names checked, in order, when reducing a related entity to a single
 * cell value. Covers the relation shapes used across the portal's CRUD tables
 * (departments, task types, statuses, employees, ...).
 */
const DISPLAY_NAME_KEYS = [
  "departmentName",
  "taskType",
  "statusName",
  "priorityName",
  "channelName",
  "areaName",
  "displayName",
  "fullName",
  "name",
  "label",
  "title",
  "username",
  "email",
] as const;

function pickDisplayName(value: Record<string, unknown>): string | null {
  for (const key of DISPLAY_NAME_KEYS) {
    const candidate = value[key];
    if (typeof candidate === "string" && candidate.trim() !== "") {
      return candidate.trim();
    }
    if (typeof candidate === "number") return String(candidate);
  }

  // Join-table rows (e.g. taskTypeAssignments) nest the entity one level down,
  // so fall back to resolving the nested relation.
  for (const key of DISPLAY_NAME_KEYS) {
    const candidate = value[key];
    if (candidate && typeof candidate === "object") {
      const nested = flattenExportValue(candidate);
      if (typeof nested === "string" && nested !== "") return nested;
    }
  }

  return null;
}

/**
 * Flattens relations into something a spreadsheet cell can hold. Without this,
 * array/object columns reach `String()` and come out as `[object Object]`.
 * Primitives and Dates are passed through untouched.
 */
function flattenExportValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;
  if (value instanceof Date) return value;

  if (Array.isArray(value)) {
    return value
      .map((entry) => flattenExportValue(entry))
      .filter((entry) => entry !== null && entry !== undefined && entry !== "")
      .join(", ");
  }

  const displayName = pickDisplayName(value as Record<string, unknown>);
  if (displayName !== null) return displayName;

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * Resolves a column's value for the tabular formats (CSV / Excel). An explicit
 * `transform` always wins; otherwise relations are flattened.
 */
function resolveTabularValue<T>(row: T, col: ExportColumn<T>): unknown {
  const rawValue = getNestedValue(row, String(col.key));
  if (col.transform) return col.transform(rawValue, row);
  return flattenExportValue(rawValue);
}

function escapeCSVValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function generateCSV<T>(data: T[], columns: ExportColumn<T>[]): string {
  const headers = columns.map((col) => escapeCSVValue(col.header)).join(",");
  const rows = data.map((row) => {
    return columns
      .map((col) => escapeCSVValue(resolveTabularValue(row, col)))
      .join(",");
  });
  // The UTF-8 BOM makes Excel and other spreadsheet programs decode
  // Hebrew/Arabic correctly instead of treating UTF-8 bytes as ANSI.
  return `\uFEFF${[headers, ...rows].join("\r\n")}`;
}

function generateJSON<T>(data: T[], columns: ExportColumn<T>[]): string {
  // Relations are intentionally left unflattened here: JSON can represent them
  // natively, and consumers are better served by the real nested objects.
  const exportData = data.map((row) => {
    const obj: Record<string, unknown> = {};
    columns.forEach((col) => {
      const rawValue = getNestedValue(row, String(col.key));
      obj[col.header] = col.transform ? col.transform(rawValue, row) : rawValue;
    });
    return obj;
  });
  return JSON.stringify(exportData, null, 2);
}

function downloadFile(
  content: string,
  filename: string,
  mimeType: string,
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function serializeCell(value: unknown): string | number | boolean {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value;
  const str = String(value);
  const lowerStr = str.toLowerCase();
  if (lowerStr === "true") return true;
  if (lowerStr === "false") return false;
  if (str !== "") {
    const num = Number(str);
    if (!isNaN(num) && String(num) === str) return num;
  }
  return str;
}

function generateXLSXBlob<T>(data: T[], columns: ExportColumn<T>[]): Blob {
  const headerRow = columns.map((col) => col.header);
  const dataRows = data.map((row) =>
    columns.map((col) => {
      const cell = serializeCell(resolveTabularValue(row, col));
      return cell === "" ? undefined : cell;
    }),
  );
  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Export");
  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

// =============================================================================
// COLUMN SELECTOR COMPONENT
// =============================================================================

interface ColumnSelectorProps<T> {
  columns: ExportColumn<T>[];
  selectedKeys: Set<string>;
  onToggle: (key: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onBack: () => void;
}

function ColumnSelector<T>({
  columns,
  selectedKeys,
  onToggle,
  onSelectAll,
  onDeselectAll,
  onBack,
}: ColumnSelectorProps<T>) {
  const allSelected = selectedKeys.size === columns.length;
  const noneSelected = selectedKeys.size === 0;
  const someSelected = !allSelected && !noneSelected;

  return (
    <div className="w-72">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
        <button
          onClick={onBack}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ChevronLeft size={16} className="text-gray-500" />
        </button>
        <Columns3 size={16} className="text-gray-500" />
        <span className="text-sm font-medium text-gray-700 flex-1">
          Select Fields
        </span>
        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
          {selectedKeys.size}/{columns.length}
        </span>
      </div>

      {/* Select All / Deselect All */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/50">
        <button
          onClick={allSelected ? onDeselectAll : onSelectAll}
          className="flex items-center gap-2 text-xs text-gray-600 hover:text-gray-900 transition-colors"
        >
          {allSelected ? (
            <CheckSquare size={14} className="text-primary-500" />
          ) : someSelected ? (
            <MinusSquare size={14} className="text-primary-500" />
          ) : (
            <Square size={14} />
          )}
          <span>{allSelected ? "Deselect All" : "Select All"}</span>
        </button>
      </div>

      {/* Column List */}
      <div className="max-h-64 overflow-y-auto py-1">
        {columns.map((col) => {
          const key = String(col.key);
          const isSelected = selectedKeys.has(key);

          return (
            <button
              key={key}
              onClick={() => onToggle(key)}
              className={`
                w-full flex items-center gap-3 px-3 py-2
                text-sm transition-colors
                ${
                  isSelected
                    ? "text-gray-900 bg-primary-500/5"
                    : "text-gray-500 hover:bg-gray-50"
                }
              `}
            >
              <div
                className={`
                w-4 h-4 rounded border flex items-center justify-center
                transition-all
                ${
                  isSelected
                    ? "bg-primary-500 border-primary-500"
                    : "border-gray-300"
                }
              `}
              >
                {isSelected && <Check size={12} className="text-white" />}
              </div>
              <span className="flex-1 text-left truncate">{col.header}</span>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
        <button
          onClick={onBack}
          disabled={selectedKeys.size === 0}
          className={`
            w-full py-2 rounded-lg text-sm font-medium
            transition-all
            ${
              selectedKeys.size > 0
                ? "bg-primary-500 text-white hover:bg-primary-700"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }
          `}
        >
          Continue with {selectedKeys.size} field
          {selectedKeys.size !== 1 ? "s" : ""}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ExportButton<T extends object>({
  data,
  resolveData,
  columns,
  filename = "export",
  formats = ["csv", "excel", "json"],
  allowColumnSelection = true,
  persistSelection = true,
  storageKey,
  onBeforeExport,
  onExportComplete,
  buttonText = "Export",
  showTextOnMobile = false,
  disabled = false,
  className = "",
  variant = "default",
  size = "md",
  disableScaleAnimation = false,
  dropdownAlign = "left",
  dropdownExtra,
}: ExportButtonProps<T>) {
  const hasExportSource = !!resolveData || data.length > 0;

  const [isOpen, setIsOpen] = useState(false);
  const [viewState, setViewState] = useState<ViewState>("formats");
  const [isExporting, setIsExporting] = useState(false);
  const [lastExported, setLastExported] = useState<ExportFormat | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Generate storage key
  const finalStorageKey = storageKey || `export-columns-${filename}`;

  // Initialize selected columns from localStorage or defaults
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<Set<string>>(
    () => {
      if (persistSelection && typeof window !== "undefined") {
        const saved = localStorage.getItem(finalStorageKey);
        if (saved) {
          try {
            const parsed = JSON.parse(saved) as string[];
            // Validate that saved keys still exist in columns
            const validKeys = new Set(columns.map((c) => String(c.key)));
            const filteredKeys = parsed.filter((k) => validKeys.has(k));
            if (filteredKeys.length > 0) {
              return new Set(filteredKeys);
            }
          } catch {
            // Invalid JSON, use defaults
          }
        }
      }
      // Default: all columns with defaultSelected !== false
      return new Set(
        columns
          .filter((c) => c.defaultSelected !== false)
          .map((c) => String(c.key)),
      );
    },
  );

  // Get selected columns in original order
  const selectedColumns = useMemo(() => {
    return columns.filter((c) => selectedColumnKeys.has(String(c.key)));
  }, [columns, selectedColumnKeys]);

  // Persist selection to localStorage
  useEffect(() => {
    if (persistSelection && typeof window !== "undefined") {
      localStorage.setItem(
        finalStorageKey,
        JSON.stringify([...selectedColumnKeys]),
      );
    }
  }, [selectedColumnKeys, persistSelection, finalStorageKey]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setViewState("formats");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getFilename = (format: ExportFormat): string => {
    const timestamp = new Date().toISOString().split("T")[0];
    return `${filename}_${timestamp}.${formatConfig[format].extension}`;
  };

  const handleToggleColumn = (key: string) => {
    setSelectedColumnKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedColumnKeys(new Set(columns.map((c) => String(c.key))));
  };

  const handleDeselectAll = () => {
    setSelectedColumnKeys(new Set());
  };

  const handleExport = async (format: ExportFormat) => {
    if (disabled || !hasExportSource || selectedColumns.length === 0) return;

    setIsExporting(true);

    try {
      if (onBeforeExport) {
        const shouldContinue = await onBeforeExport(format, selectedColumns);
        if (!shouldContinue) {
          setIsExporting(false);
          return;
        }
      }

      const exportData = resolveData ? await resolveData() : data;
      if (exportData.length === 0) return;

      if (format === "excel") {
        const blob = generateXLSXBlob(exportData, selectedColumns);
        downloadBlob(blob, getFilename(format));
      } else {
        let content: string;
        let mimeType: string;

        switch (format) {
          case "csv":
            content = generateCSV(exportData, selectedColumns);
            mimeType = "text/csv;charset=utf-8;";
            break;
          case "json":
            content = generateJSON(exportData, selectedColumns);
            mimeType = "application/json;charset=utf-8;";
            break;
          default:
            throw new Error(`Unsupported format: ${format}`);
        }

        downloadFile(content, getFilename(format), mimeType);
      }

      setLastExported(format);
      setTimeout(() => setLastExported(null), 2000);

      onExportComplete?.(format, selectedColumns);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setIsExporting(false);
      setIsOpen(false);
      setViewState("formats");
    }
  };

  // Simple button for single format without column selection
  if (formats.length === 1 && !allowColumnSelection) {
    const format = formats[0];
    return (
      <motion.button
        onClick={() => handleExport(format)}
        disabled={disabled || isExporting || !hasExportSource}
        whileHover={disableScaleAnimation ? undefined : { scale: 1.02 }}
        whileTap={disableScaleAnimation ? undefined : { scale: 0.98 }}
        className={`
          flex items-center justify-center
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          rounded-xl font-medium
          transition-all
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
      >
        {isExporting ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Download size={16} />
          </motion.div>
        ) : (
          formatConfig[format].icon
        )}
        <span className={showTextOnMobile ? "" : "hidden sm:inline"}>
          {buttonText}
        </span>
      </motion.button>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isExporting || !hasExportSource}
        whileHover={disableScaleAnimation ? undefined : { scale: 1.02 }}
        whileTap={disableScaleAnimation ? undefined : { scale: 0.98 }}
        className={`
          flex items-center justify-center
          ${sizeClasses[size]}
          ${variantClasses[variant]}
          rounded-xl font-medium
          transition-all
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
      >
        {isExporting ? (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <Download size={16} />
          </motion.div>
        ) : (
          <Download size={16} />
        )}
        <span className={showTextOnMobile ? "" : "hidden sm:inline"}>
          {buttonText}
        </span>
        <ChevronDown
          size={14}
          className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`
              absolute ${dropdownAlign === "right" ? "right-0" : "left-0"} mt-2 z-[100]
              bg-white rounded-xl
              border border-gray-200
              shadow-lg shadow-black/10
              overflow-hidden
            `}
          >
            <AnimatePresence mode="wait">
              {viewState === "formats" ? (
                <motion.div
                  key="formats"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.15 }}
                  className="min-w-[240px]"
                >
                  {/* Configure Fields Button */}
                  {allowColumnSelection && (
                    <div className="p-1 border-b border-gray-100">
                      <button
                        onClick={() => setViewState("columns")}
                        className="
                          w-full flex items-center gap-3 px-3 py-2.5
                          text-sm text-gray-700
                          hover:bg-gray-50 rounded-lg
                          transition-colors
                        "
                      >
                        <Settings2 size={16} className="text-gray-500" />
                        <span className="flex-1 text-left">
                          Configure Fields
                        </span>
                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                          {selectedColumns.length}
                        </span>
                      </button>
                    </div>
                  )}

                  {dropdownExtra && (
                    <div className="p-2 border-b border-gray-100">{dropdownExtra}</div>
                  )}

                  {/* Format Options */}
                  <div className="p-1">
                    {formats.map((format) => (
                      <button
                        key={format}
                        onClick={() => handleExport(format)}
                        disabled={selectedColumns.length === 0}
                        className={`
                          w-full flex items-center gap-3 px-3 py-2.5
                          text-sm
                          rounded-lg transition-colors
                          ${
                            selectedColumns.length === 0
                              ? "text-gray-400 cursor-not-allowed"
                              : "text-gray-700 hover:bg-gray-50"
                          }
                        `}
                      >
                        <span className="text-gray-500">
                          {formatConfig[format].icon}
                        </span>
                        <span className="flex-1 text-left">
                          Export as {formatConfig[format].label}
                        </span>
                        {lastExported === format && (
                          <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-green-500"
                          >
                            <Check size={14} />
                          </motion.span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500">
                    {resolveData
                      ? "All rows on export"
                      : `${data.length} row${data.length !== 1 ? "s" : ""}`}{" "}
                    • {selectedColumns.length} field
                    {selectedColumns.length !== 1 ? "s" : ""}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="columns"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.15 }}
                >
                  <ColumnSelector
                    columns={columns}
                    selectedKeys={selectedColumnKeys}
                    onToggle={handleToggleColumn}
                    onSelectAll={handleSelectAll}
                    onDeselectAll={handleDeselectAll}
                    onBack={() => setViewState("formats")}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ExportButton;
